import {
  useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type ReactNode, type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useSchliessanfrage } from "../schale/schliessanfrage";
import { Zeichen } from "./Zeichen";
import { useT } from "../i18n";
import { alsTag } from "../daten/kalender";
import { useTelefon } from "../schale/geraet";
import { Tagesblatt, Zeitraumblatt } from "../masken/handy/Zeitraumblatt";
import { heuteImBetrieb, tagImBetrieb } from "../daten/betriebstag";
import { kalenderKlick, spanneAusText, tagAusText } from "../daten/zeitraumeingabe";
import { useFeiertagAm } from "./Feiertage";

/* ══════════════════════════════════════════════════════════════════
   Der Kalender der Anwendung — für einen Zeitraum, einen Tag, einen Tag
   mit Uhrzeit.

   Im Prototyp stand hier ein Textfeld mit „04.05. – 24.05.2026" fest
   eingetragen — ein Bild, keine Bedienung. Am Telefon ist genau das der
   Engpass: Der Kunde sagt „ab Montag für zwei Wochen", und wer dann ein
   Datum tippen muss, hält das Gespräch auf.

   Drei Wege hinein, und alle drei führen zum selben Ergebnis:

     · **Tippen** — „12.10." oder „12.10.-16.10." oder „12.10.26"
     · **Aus der Spalte wählen** — „1 Woche", „Dieses Jahr", „Morgen"
     · **Im Kalender klicken** — beim Zeitraum erst der Anfang, dann das Ende

   ## Ein Aussehen, überall (18.09.2026)

   Luca: *„Überprüfe mal im kompletten Projekt, wo noch Date Picker gebaut
   sind, und nimm dann komplett den, den du jetzt gerade gebaut hast, weil
   der deutlich schöner ist."* Gemeint ist die Form der Belegsuche: **die
   Auswahl als schmale Spalte links, der Kalender rechts.** Bis dahin
   standen die Mietdauern als Pillenreihe über den Monaten — breiter als
   die Monate selbst, mit einem leeren Streifen rechts —, und an vierzehn
   Stellen stand statt eines Kalenders das Datumsfeld des Browsers, das in
   jedem Browser anders aussieht und „tt.mm.jjjj" hineinschreibt.

   Jetzt gibt es **ein** Blatt (`Kalenderblatt`) und drei Felder darüber:

   | Baustein | wofür | Spalte links |
   |---|---|---|
   | `Zeitraum` | vermieten, suchen | Dauern ab dem Anfang — oder, als Suche, „Dieser Monat" … |
   | `Datum` | ein Tag: Frist, Prüfung, Anschaffung | „Heute", „Morgen" … — vor oder zurück |
   | `DatumZeit` | ein Zeitpunkt: die Übergabe | wie `Datum`, dazu die Uhrzeit |

   ## Am Telefon ein Blatt von unten (20.09.2026)

   Luca, beim Angebot am Telefon: *„Wenn ich … eine Position hinzufüge und
   ich das Datum ändere, ist wieder der Fokus auf der Tastatur, wodurch ich
   richtig beschissen das Datum picken kann. Der Date Picker ist sowieso an
   dieser Stelle sehr, sehr schlecht eingebettet und macht da dann einfach …
   das als Bottom Sheet oder so."*

   Das Feld oben **ist** ein Textfeld, und am Telefon holt jedes Textfeld
   die Tastatur, sobald es den Fokus bekommt — und den Fokus bekommt es hier
   vom Öffnen des Kalenders. Dasselbe stand am 18.09.2026 schon einmal an
   (`bausteine/Blattvonunten.tsx`, „Der Fokus geht auf das Blatt, nie in ein
   Feld"), und dort ist es gelöst; nur ging es an diesem Feld vorbei, weil
   der Kalender nicht im Blatt lag, sondern unter dem Feld schwebte.

   **Also fragen `Zeitraum` und `Datum` zuerst die Lage** (`useTelefon()`).
   Am Telefon ist der Auslöser ein **Knopf**, der aussieht wie das Feld
   (`Kalenderknopf`), und der Kalender ist das Blatt von unten, das das
   Nachschlagen und die Sammlung schon führen (`masken/handy/Zeitraumblatt.tsx`
   — Luca dazu: *„Ich kann jetzt relativ gut Zeiträume auswählen"*). Ein
   Knopf hat keine Tastatur. Wer den Tag lieber tippt, tut das im Fuß des
   Blattes **ausdrücklich** (`Datumsblatt`), nicht als Nebenwirkung.

   Die Masken merken davon nichts: dieselben Eigenschaften, derselbe Wert.
   Am Schreibtisch bleibt alles, wie es war — ein zweiter Kalender für denselben
   Zweck liefe beim dritten Sonderfall auseinander, also gibt es je Lage genau
   einen.
   ══════════════════════════════════════════════════════════════════ */

export type Spanne = { von: string | null; bis: string | null };

function ausIso(iso: string): Date {
  const [j, m, t] = iso.split("-").map(Number);
  return new Date(j ?? 1970, (m ?? 1) - 1, t ?? 1);
}

function plusTage(iso: string, tage: number): string {
  const d = ausIso(iso);
  d.setDate(d.getDate() + tage);
  return alsTag(d);
}

/** Derselbe Tag n Monate später — der 31.01. plus einen Monat ist der 28.02., nicht der 03.03. */
function plusMonate(iso: string, monate: number): string {
  const d = ausIso(iso);
  const ziel = new Date(d.getFullYear(), d.getMonth() + monate, 1);
  const letzter = new Date(ziel.getFullYear(), ziel.getMonth() + 1, 0).getDate();
  ziel.setDate(Math.min(d.getDate(), letzter));
  return alsTag(ziel);
}

/** `12.10.2026` — so steht ein Tag im Feld. */
function tagText(iso: string): string {
  const d = ausIso(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

/** Wie viele Kalendertage der Zeitraum umfasst, Anfang und Ende mitgezählt. */
export function tageIn(spanne: Spanne): number {
  if (!spanne.von || !spanne.bis) return 0;
  const a = ausIso(spanne.von).getTime();
  const b = ausIso(spanne.bis).getTime();
  return Math.max(0, Math.round((b - a) / 86_400_000) + 1);
}

/** Die Tage eines Monatsblatts — vom Montag vor dem Ersten bis zum Schluss. */
function monatsblatt(jahr: number, monat: number): Date[] {
  const erster = new Date(jahr, monat, 1);
  const anfang = new Date(erster);
  anfang.setDate(1 - ((erster.getDay() + 6) % 7));
  const tage: Date[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(anfang);
    d.setDate(anfang.getDate() + i);
    tage.push(d);
    if (i >= 34 && d.getMonth() !== monat && d > erster) break;
  }
  return tage;
}

/** Abstand des Blatts zum Feld und zum Rand des Fensters. */
const LUFT = 6;
const RAND = 16;
/** So breit steht das Blatt mindestens. */
const BREITE = 340;

/**
 * Wo das Blatt steht: unter dem Feld, und wenn darunter kein Platz ist,
 * darüber. Gerechnet am Fenster, nicht am Elternelement.
 *
 * **Warum am Fenster.** Bis zum 16.09.2026 hing das Blatt `absolute` unter
 * dem Feld. In der Schnellerfassung ging das; in der Positionstabelle des
 * Angebots nicht — die Tabelle hat einen Rahmen mit `overflow: hidden` und
 * ein Fenster mit `overflow: auto`, und darin wurde das Blatt abgeschnitten.
 * Luca, 16.09.2026: *„Den Zeitraum kann ich nicht, weil ich dann scrollen
 * muss. Das muss ja dann überlagert sein."* Jetzt hängt es am `body` und
 * steht `fixed` — kein Rahmen darüber kann es mehr kappen.
 */
function lageAm(feld: HTMLElement, blatt: HTMLElement | null): CSSProperties {
  const r = feld.getBoundingClientRect();
  const hoechstens = window.innerWidth - 2 * RAND;
  const mindestens = Math.min(Math.max(BREITE, r.width), hoechstens);
  /* Die Breite gibt der Inhalt vor — die Monate brauchen, was sie brauchen.
     Gesetzt werden nur die Grenzen, und links wird so weit eingerückt, dass
     das Blatt nicht aus dem Fenster ragt. */
  const breite = Math.min(Math.max(blatt?.offsetWidth ?? 0, mindestens), hoechstens);
  const links = Math.max(RAND, Math.min(r.left, window.innerWidth - RAND - breite));
  const hoehe = blatt?.offsetHeight ?? 0;
  const untenFrei = window.innerHeight - r.bottom - LUFT - RAND;
  const obenFrei = r.top - LUFT - RAND;
  const nachOben = hoehe > untenFrei && obenFrei > untenFrei;
  return {
    position: "fixed",
    left: links,
    minWidth: mindestens,
    maxWidth: hoechstens,
    ...(nachOben
      ? { bottom: window.innerHeight - r.top + LUFT }
      : { top: r.bottom + LUFT }),
    maxHeight: Math.max(nachOben ? obenFrei : untenFrei, 160),
    overflowY: "auto",
  };
}

/* ══════════════ Das Blatt ══════════════ */

/** Ein Eintrag der Spalte links. */
type Wahl = { text: string; gewaehlt: boolean; waehlen: () => void };

type Blattstand = { jahr: number; monat: number };

/**
 * Offen, zu, wo — und wann es zugeht. Für alle drei Felder derselbe Haken.
 */
function useKlappe(): {
  offen: boolean;
  setzeOffen: (an: boolean) => void;
  raum: RefObject<HTMLDivElement | null>;
  feld: RefObject<HTMLDivElement | null>;
  blattRaum: RefObject<HTMLDivElement | null>;
  lage: CSSProperties;
  nachmessen: () => void;
} {
  const [offen, setzeOffen] = useState(false);
  const raum = useRef<HTMLDivElement>(null);
  const feld = useRef<HTMLDivElement>(null);
  const blattRaum = useRef<HTMLDivElement>(null);
  const [lage, setzeLage] = useState<CSSProperties>({ position: "fixed", visibility: "hidden" });
  const nachmessen = () => {
    if (feld.current) setzeLage(lageAm(feld.current, blattRaum.current));
  };

  /* Die Lage folgt dem Feld: beim Öffnen, beim Scrollen irgendeines
     Vorfahren (darum `capture`) und wenn das Fenster seine Größe ändert. */
  useLayoutEffect(() => {
    if (!offen) return;
    const setzen = () => {
      if (feld.current) setzeLage(lageAm(feld.current, blattRaum.current));
    };
    setzen();
    window.addEventListener("scroll", setzen, true);
    window.addEventListener("resize", setzen);
    return () => {
      window.removeEventListener("scroll", setzen, true);
      window.removeEventListener("resize", setzen);
    };
  }, [offen]);

  // Ein Kalender, der offen bleibt, wenn man daneben klickt, ist einer,
  // den man wegklicken will und nicht kann.
  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => {
      const ziel = e.target as Node;
      // Das Blatt hängt am `body`, nicht im Feld — beides zählt als drinnen.
      if (!raum.current?.contains(ziel) && !blattRaum.current?.contains(ziel)) setzeOffen(false);
    };
    document.addEventListener("mousedown", zu);
    return () => {
      document.removeEventListener("mousedown", zu);
    };
  }, [offen]);

  /* **`Esc` schließt erst den Kalender, dann das Fenster** (08.10.2026). Im
     Abschluss „Jetzt vermieten" schloss `Esc` bei offenem Kalender das ganze
     Fenster: Das Deckblatt hört in der Fangphase auf die Taste
     (`schale/schliessanfrage.ts`), und der Kalender stand nicht auf dem
     Stapel. Jetzt steht er obenauf, solange er offen ist — dieselbe Anfrage
     wie jedes Blatt. */
  useSchliessanfrage(offen, () => setzeOffen(false));

  return { offen, setzeOffen, raum, feld, blattRaum, lage, nachmessen };
}

/**
 * Das Blatt unter dem Feld: links die Spalte, rechts Blättern, Monate und
 * ein Satz dazu.
 *
 * **Die Spalte steht links und nicht als Reihe über den Monaten** (Luca,
 * 18.09.2026). Als Reihe war sie breiter als die Monate darunter, und rechts
 * blieb ein leerer Streifen. Untereinander nimmt sie eine schmale Spalte und
 * liest sich wie das, was sie ist: eine Wahl aus wenigen. Schmal (unter
 * 760 px) steht sie als umbrechende Reihe über dem Kalender — neben einem
 * Monat wäre dort kein Platz (`stile/schnell.css`).
 */
function Kalenderblatt({
  blattRaum, lage, schild, wahlen, blatt, setzeBlatt, monate: anzahl, tag, heute, satz, rueckblick = false,
}: {
  blattRaum: RefObject<HTMLDivElement | null>;
  lage: CSSProperties;
  /** i18n-Schlüssel über der Spalte, etwa „Dauer". Ohne ihn steht die Spalte für sich. */
  schild?: string | undefined;
  wahlen: Wahl[];
  blatt: Blattstand;
  setzeBlatt: (neu: Blattstand) => void;
  /** Wie viele Monate nebeneinander stehen: zwei für einen Zeitraum, einer für einen Tag. */
  monate: 1 | 2;
  /** Wie ein Tag aussieht und was ein Klick auf ihn tut. */
  tag: (iso: string) => { rand: boolean; drin: boolean; klick: () => void };
  /** Was „Heute" tut — blättern, oder zusätzlich wählen. */
  heute: () => void;
  satz: string;
  /** Rückwärts gesucht: Schmal steht dann der jüngere Monat oben (`schnell.css`). */
  rueckblick?: boolean;
}) {
  const t = useT();
  const monatsnamen = t.liste("kalender.monate");
  const wochentage = t.liste("kalender.wochentageKurz");
  const heuteIso = heuteImBetrieb();
  /* Feiertage im Blatt markiert, der Name im `title` (0287) — nur Anzeige. */
  const feiertagAm = useFeiertagAm();

  const schieben = (um: number) => {
    const d = new Date(blatt.jahr, blatt.monat + um, 1);
    setzeBlatt({ jahr: d.getFullYear(), monat: d.getMonth() });
  };

  const monatsTafel = (versatz: number) => {
    const d = new Date(blatt.jahr, blatt.monat + versatz, 1);
    const jahr = d.getFullYear();
    const monat = d.getMonth();
    return (
      <div className="zr-monat" key={`${jahr}-${monat}`}>
        <div className="zr-monat-kopf">{monatsnamen[monat]} {jahr}</div>
        <div className="zr-gitter">
          {wochentage.map((w, i) => <div className="zr-wt" key={`w${i}`}>{w}</div>)}
          {monatsblatt(jahr, monat).map((tagD) => {
            const iso = alsTag(tagD);
            const art = tag(iso);
            const klassen = ["zr-tag"];
            if (tagD.getMonth() !== monat) klassen.push("fremd");
            if (art.rand) klassen.push("rand");
            if (art.drin) klassen.push("drin");
            if (iso === heuteIso) klassen.push("heute");
            const feiertag = feiertagAm(iso);
            if (feiertag) klassen.push("feiertag");
            return (
              <button
                key={iso}
                type="button"
                className={klassen.join(" ")}
                onClick={art.klick}
                aria-label={feiertag ? `${iso} ${feiertag}` : iso}
                {...(feiertag ? { title: feiertag } : {})}
                {...(art.rand ? { "aria-pressed": true as const } : {})}
              >
                {tagD.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return createPortal(
    <div className={rueckblick ? "zr-blatt rueck" : "zr-blatt"} ref={blattRaum} style={lage}>
      <div className="zr-rumpf">
        <div className="zr-schnell">
          {schild ? <span className="zr-schild">{t(schild)}</span> : null}
          {wahlen.map((w) => (
            <button
              key={w.text}
              type="button"
              className="zr-wahl"
              aria-pressed={w.gewaehlt}
              onClick={w.waehlen}
            >
              {t(w.text)}
            </button>
          ))}
        </div>
        <div className="zr-kalender">
          <div className="zr-navi">
            <button type="button" className="zr-pfeil" onClick={() => schieben(-1)}
              aria-label={t("zeitraum.zurueck")}>‹</button>
            <button type="button" className="chip" onClick={heute}>{t("zeitraum.heute")}</button>
            <button type="button" className="zr-pfeil" onClick={() => schieben(1)}
              aria-label={t("zeitraum.vor")}>›</button>
          </div>
          <div className="zr-monate">
            {monatsTafel(0)}
            {anzahl === 2 ? monatsTafel(1) : null}
          </div>
          <div className="zr-fuss">{satz}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Das Feld: das Kalenderzeichen links, das Textfeld, rechts eine Zahl.
 *
 * **Das Zeichen aus dem Satz** (`bausteine/Zeichen.tsx`, 17.09.2026). Hier
 * stand ein Kalender aus einem Rahmen und drei Schatten — gezeichnet in
 * CSS, weil es schnell ging, und dadurch das einzige Zeichen der Anwendung,
 * das nicht aus dem Satz kam. Ein zweiter Weg, ein Zeichen zu zeichnen, ist
 * einer zu viel.
 */
function Kalenderfeld({
  feldRaum, id, text, setzeText, uebernehmen, oeffnen, schliessen, platz, rechts, klein, gesperrt,
  titel, schild, ungueltig, beschrieben, klasse,
}: {
  feldRaum: RefObject<HTMLDivElement | null>;
  id: string;
  text: string;
  setzeText: (neu: string) => void;
  uebernehmen: () => void;
  oeffnen: () => void;
  schliessen: () => void;
  platz: string;
  rechts?: ReactNode;
  klein?: boolean | undefined;
  gesperrt?: boolean | undefined;
  titel?: string | undefined;
  schild?: string | undefined;
  ungueltig?: boolean | undefined;
  /** Die Kennung des Satzes unter dem Feld, der sagt, was nicht stimmt (N12-03). */
  beschrieben?: string | undefined;
  klasse?: string | undefined;
}) {
  const klassen = [klein ? "fein" : "feld", rechts ? "" : "zr-ohne-tage", klasse ?? ""].filter(Boolean).join(" ");
  const frischFokussiert = useRef(false);
  return (
    <div className={`zr-feld${klein ? " klein" : ""}`} ref={feldRaum}>
      <Zeichen name="zeitraum" />
      <input
        className={klassen}
        id={id}
        autoComplete="off"
        placeholder={platz}
        value={text}
        disabled={gesperrt}
        {...(titel ? { title: titel } : {})}
        {...(schild ? { "aria-label": schild } : {})}
        {...(ungueltig ? { "aria-invalid": true as const } : {})}
        {...(ungueltig && beschrieben ? { "aria-describedby": beschrieben } : {})}
        onChange={(e) => setzeText(e.target.value)}
        /* **Beim Hineinklicken ist der Wert markiert** (Prüfrunde Runde 11,
           „Neu gesehen"): Getippt wird dann ein neuer Zeitraum, nicht ein
           Anhang an den alten. Das Loslassen der Maus nähme die Markierung
           wieder weg — darum wird es einmal, direkt nach dem Fokus,
           übergangen. */
        onFocus={(e) => { e.currentTarget.select(); frischFokussiert.current = true; oeffnen(); }}
        onMouseUp={(e) => { if (frischFokussiert.current) { e.preventDefault(); frischFokussiert.current = false; } }}
        onBlur={() => { frischFokussiert.current = false; uebernehmen(); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); uebernehmen(); schliessen(); }
          /* **Tab schließt das Blatt** (P8-06, 05.10.2026). Bis dahin blieb
             es offen, wenn der Fokus mit Tab weiterging — zur Uhrzeit
             daneben. Es lag dann über dem Blatt darunter, und der nächste
             Klick dorthin traf „Vor einem Jahr": Getippt war 02.10.2026,
             geschickt 05.10.2025, und die Meldung sprach von der Reihenfolge
             Übergabe/Rücknahme. Das Getippte übernimmt `onBlur`. */
          if (e.key === "Tab") schliessen();
        }}
      />
      {rechts}
    </div>
  );
}

/**
 * Das Feld am Telefon: **ein Knopf, der aussieht wie das Feld.**
 *
 * Dasselbe Zeichen links, derselbe Rahmen, derselbe Platz in der Maske —
 * nur dass es ein `<button>` ist und kein `<input>`. Der Unterschied ist
 * die Tastatur: Ein Knopf holt keine. Rechts ein Winkel, wie an jeder
 * Zeile am Telefon, die etwas öffnet (`hw-griff-pfeil`); die Zahl der
 * Miettage steht davor, wo sie im Feld auch stand.
 *
 * Ein `<label htmlFor>` der Maske zeigt weiter auf die `id` — ein Knopf ist
 * ein beschriftbares Element, und wer die Beschriftung antippt, öffnet den
 * Kalender. `aria-haspopup="dialog"` sagt dem Vorleser, was kommt.
 */
function Kalenderknopf({ id, text, platz, oeffnen, offen, rechts, klein, gesperrt, titel, schild, ungueltig, klasse }: {
  id: string;
  text: string;
  platz: string;
  oeffnen: () => void;
  offen: boolean;
  rechts?: ReactNode;
  klein?: boolean | undefined;
  gesperrt?: boolean | undefined;
  titel?: string | undefined;
  schild?: string | undefined;
  ungueltig?: boolean | undefined;
  klasse?: string | undefined;
}) {
  const klassen = [klein ? "fein" : "feld", "zr-knopf", klasse ?? ""].filter(Boolean).join(" ");
  return (
    <div className={`zr-feld${klein ? " klein" : ""}`}>
      <Zeichen name="zeitraum" />
      <button
        type="button"
        className={klassen}
        id={id}
        disabled={gesperrt}
        aria-haspopup="dialog"
        aria-expanded={offen}
        {...(titel ? { title: titel } : {})}
        {...(schild ? { "aria-label": schild } : {})}
        {...(ungueltig ? { "aria-invalid": true as const } : {})}
        onClick={oeffnen}
      >
        <span className={text ? "zr-knopf-text" : "zr-knopf-text leer"}>{text || platz}</span>
        {rechts}
        <span className="zr-knopf-pfeil" aria-hidden="true" />
      </button>
    </div>
  );
}

/* ══════════════ Zeitraum ══════════════ */

const DAUERN = [
  { tage: 1, text: "zeitraum.dauer1" },
  { tage: 3, text: "zeitraum.dauer3" },
  { tage: 5, text: "zeitraum.dauer5" },
  { tage: 7, text: "zeitraum.dauer7" },
  { tage: 14, text: "zeitraum.dauer14" },
  { tage: 30, text: "zeitraum.dauer30" },
];

/**
 * Welche zwei Monate das Blatt zeigt.
 *
 * Vorwärts (vermieten): der Monat des Anfangs und der nächste. **Im
 * Rückblick** (suchen): der Monat des Endes rechts, der davor links — und
 * nie über heute hinaus. „Dieses Jahr" reicht bis zum 31.12.; wer das Blatt
 * im September öffnet, will August und September sehen, nicht November und
 * Dezember, in denen noch kein Beleg liegt.
 */
function blattZu(spanne: Spanne, rueckblick: boolean): Blattstand {
  const heute = heuteImBetrieb();
  if (!rueckblick) {
    const d = spanne.von ? ausIso(spanne.von) : new Date();
    return { jahr: d.getFullYear(), monat: d.getMonth() };
  }
  const ende = spanne.bis ?? spanne.von ?? heute;
  const d = ausIso(ende < heute ? ende : heute);
  const links = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return { jahr: links.getFullYear(), monat: links.getMonth() };
}

/**
 * Ein Zeitraum in einem Griff — für die Suche statt der Mietdauern.
 *
 * `spanne` mit zwei `null` heißt „jederzeit" und nimmt den Filter zurück.
 */
export type Schnellwahl = { text: string; spanne: Spanne };

type ZeitraumEigenschaften = {
  wert: Spanne;
  aendern: (neu: Spanne) => void;
  id?: string;
  /**
   * **Die Suchfassung** (18.09.2026, Belegsuche). Wer vermietet, wählt eine
   * Dauer ab einem Anfang; wer einen Beleg sucht, fragt „letzter Monat" oder
   * „dieses Jahr". Steht `schnell` da, ersetzt es die Dauern — und die Zahl
   * der Miettage im Feld fällt weg, weil sie bei einer Suche nichts sagt.
   * Tippen und Kalender bleiben dieselben.
   */
  schnell?: Schnellwahl[];
  /** Der Kalender zeigt Vormonat und diesen statt diesen und den nächsten — gesucht wird rückwärts. */
  rueckblick?: boolean;
  /** i18n-Schlüssel des Platzhalters. */
  platz?: string;
  /** Nicht änderbar — mit `titel` als Grund. */
  gesperrt?: boolean | undefined;
  titel?: string | undefined;
  /** Das `aria-label`, wo kein `<label htmlFor>` darauf zeigt — am Telefon auch der Titel des Blattes. */
  schild?: string | undefined;
  /** Das kleine Feld (`.fein`) — für eine Tabellenzeile. */
  klein?: boolean | undefined;
  /**
   * Ohne die Zahl der Tage rechts im Feld (Vermieten, 07.10.2026): Dort
   * steht der Zeitraum genau einmal, und die Tage nennt jede Trefferzeile.
   */
  ohneTage?: boolean | undefined;
  /**
   * **Nur ganze Zeiträume nach außen** (08.10.2026). Wer den Wert an einen
   * Posten schreibt, kann mit „Anfang ohne Ende" nichts anfangen und warf
   * ihn weg — und weil das Feld seinen Wert vom Aufrufer liest, war der
   * erste Klick im Kalender damit verloren: Der zweite setzte das Ende zum
   * ALTEN Anfang (Abschluss „Jetzt vermieten", Merkliste rechts). Mit
   * `ganz` hält das Feld den angefangenen Zeitraum selbst, bis das Ende
   * gewählt ist, und meldet erst dann.
   */
  ganz?: boolean | undefined;
};

/** „12.10. – 16.10.2026" — so steht ein Zeitraum im Feld. */
function spannenText(wert: Spanne): string {
  if (!wert.von) return "";
  const a = ausIso(wert.von);
  const kurz = (d: Date) => `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
  if (!wert.bis || wert.bis === wert.von) return `${kurz(a)}${a.getFullYear()}`;
  const b = ausIso(wert.bis);
  /* **Das Jahr steht vorn, wenn die beiden verschiedene haben.** Sonst las
     sich „05.05. – 07.05.2031" wie ein Zeitraum im Mai 2031 — und es waren
     fünf Jahre. Über den Jahreswechsel zu mieten ist selten, aber wer es
     tut, soll es am Feld sehen und nicht erst an der Zahl der Miettage. */
  const jahrVorn = a.getFullYear() === b.getFullYear() ? "" : String(a.getFullYear());
  return `${kurz(a)}${jahrVorn} – ${kurz(b)}${b.getFullYear()}`;
}

/**
 * Der Zeitraum — am Schreibtisch das Feld mit dem schwebenden Blatt, am
 * Telefon der Knopf mit dem Blatt von unten („Am Telefon ein Blatt von
 * unten" oben). Zwei Bausteine mit eigenen Haken, deshalb die Weiche davor
 * und kein `if` mittendrin: Wer das Gerät dreht, wechselt den Baustein und
 * nicht die Reihenfolge der Haken.
 */
export function Zeitraum(e: ZeitraumEigenschaften) {
  return useTelefon() ? <ZeitraumAmTelefon {...e} /> : <ZeitraumFeld {...e} />;
}

/**
 * Am Telefon: der Knopf und das Blatt des Nachschlagens
 * (`masken/handy/Zeitraumblatt.tsx`). Die Suchfassung (`schnell`) bekommt
 * dasselbe Blatt mit einem Jahr Rückblick — die Schnellwahlen stehen in der
 * Belegsuche ohnehin als Reihe über dem Feld.
 */
function ZeitraumAmTelefon({
  wert, aendern, id = "zeitraum", schnell, rueckblick = false, platz = "zeitraum.platz",
  gesperrt, titel, schild, klein, ohneTage = false,
}: ZeitraumEigenschaften) {
  const t = useT();
  const [offen, setzeOffen] = useState(false);
  const anzeige = spannenText(wert);
  const tage = schnell || ohneTage ? 0 : tageIn(wert);
  const heute = heuteImBetrieb();
  /* Rückwärts gesucht: ein Jahr zurück, und weiter, wenn der gewählte
     Anfang davor liegt — ein Tag, der gewählt ist, darf nicht gesperrt sein. */
  const jahrZurueck = plusMonate(heute, -12);
  const ab = rueckblick ? (wert.von && wert.von < jahrZurueck ? wert.von : jahrZurueck) : undefined;

  return (
    <div className="zr">
      <Kalenderknopf
        id={id}
        text={anzeige}
        platz={t(platz)}
        oeffnen={() => setzeOffen(true)}
        offen={offen}
        rechts={tage > 0 && !klein ? <span className="zr-knopf-tage">{t("zeitraum.tage", { zahl: tage })}</span> : null}
        klein={klein}
        gesperrt={gesperrt}
        titel={titel}
        schild={schild}
      />
      {/* Erst beim Öffnen gebaut — der Entwurf im Blatt beginnt bei dem, was
          gerade gilt (`Zeitraumwahl` macht es genauso). */}
      {offen ? (
        <Zeitraumblatt
          von={wert.von ?? heute}
          bis={wert.bis ?? wert.von ?? heute}
          titel={schild}
          ab={ab}
          schliessen={() => setzeOffen(false)}
          uebernehmen={(von, bis) => { aendern({ von, bis }); setzeOffen(false); }}
        />
      ) : null}
    </div>
  );
}

function ZeitraumFeld({
  id = "zeitraum",
  schnell,
  rueckblick = false,
  platz = "zeitraum.platz",
  gesperrt,
  titel,
  schild,
  klein,
  ohneTage = false,
  ganz = false,
  wert: aussen,
  aendern: melden,
}: ZeitraumEigenschaften) {
  const t = useT();
  const k = useKlappe();
  /* Der angefangene Zeitraum, solange das Ende fehlt (`ganz`). Er gilt nur,
     solange der Kalender offen ist — wer ihn ohne Ende zumacht, behält den
     alten Zeitraum. */
  const [angefangen, setzeAngefangen] = useState<Spanne | null>(null);
  useEffect(() => { if (!k.offen) setzeAngefangen(null); }, [k.offen]);
  const wert = ganz && angefangen ? angefangen : aussen;
  const aendern = (neu: Spanne) => {
    if (ganz && (!neu.von || !neu.bis)) {
      setzeAngefangen(neu.von ? neu : null);
      return;
    }
    setzeAngefangen(null);
    melden(neu);
  };
  const [text, setzeText] = useState("");
  const [blatt, setzeBlatt] = useState(() => blattZu(wert, rueckblick));

  const anzeige = useMemo(() => spannenText(wert), [wert]);

  /** Was getippt wurde, ließ sich nicht lesen — das Feld sagt es, statt still zurückzuspringen. */
  const [falsch, setzeFalsch] = useState(false);
  const hinweisId = useId();
  useEffect(() => { setzeText(anzeige); setzeFalsch(false); }, [anzeige]);
  // Ein Monat mit sechs Wochen ist eine Zeile höher — dann kann das Blatt
  // über das Fenster hinausragen und muss nach oben.
  useLayoutEffect(() => { if (k.offen) k.nachmessen(); }, [blatt]);

  const tage = schnell || ohneTage ? 0 : tageIn(wert);

  /** Der Anfang ist eben im Kalender gesetzt — der nächste Klick ist das Ende. */
  const [endeFolgt, setzeEndeFolgt] = useState(false);
  useEffect(() => { if (!k.offen) setzeEndeFolgt(false); }, [k.offen]);


  /** Getippt wird frei; ausgewertet wird beim Verlassen und bei Enter (`daten/zeitraumeingabe.ts`). */
  const textUebernehmen = () => {
    const gelesen = spanneAusText(text, anzeige, new Date().getFullYear());
    /* **Nicht still zurückspringen** (Prüfrunde Runde 11, „Neu gesehen"):
       Wer ins Feld klickte und tippte, schrieb hinter den vorbelegten Tag
       („06.10.202613.10.2026 – …"), und das Feld sprang ohne ein Wort auf
       den alten Wert. Jetzt bleibt der Text stehen und das Feld wird rot —
       und beim Hineinklicken ist der alte Wert markiert, sodass Tippen ihn
       ersetzt (`Kalenderfeld`). */
    setzeFalsch(gelesen.art === "falsch");
    // Ein Posten ohne Zeitraum gibt es nicht (`ganz`): Leeren stellt den alten wieder hin.
    if (gelesen.art === "leer") {
      if (ganz) setzeText(anzeige);
      else aendern({ von: null, bis: null });
    }
    if (gelesen.art === "spanne") aendern({ von: gelesen.von, bis: gelesen.bis });
  };

  /* Die Spalte: als Suche die Zeiträume, die der Aufrufer nennt; sonst die
     Dauern ab dem Anfang — „ab heute für eine Woche" ist der häufigste Satz
     am Telefon, und er ist dann ein Klick. */
  const wahlen: Wahl[] = schnell
    ? schnell.map((w) => ({
        text: w.text,
        gewaehlt: w.spanne.von === wert.von && w.spanne.bis === wert.bis,
        waehlen: () => {
          aendern(w.spanne);
          setzeBlatt(blattZu(w.spanne, rueckblick));
          k.setzeOffen(false);
        },
      }))
    : DAUERN.map((d) => ({
        text: d.text,
        gewaehlt: Boolean(wert.von && wert.bis && wert.bis === plusTage(wert.von, d.tage - 1)),
        waehlen: () => {
          const von = wert.von ?? heuteImBetrieb();
          aendern({ von, bis: plusTage(von, d.tage - 1) });
          k.setzeOffen(false);
        },
      }));

  return (
    <div className="zr" ref={k.raum}>
      <Kalenderfeld
        feldRaum={k.feld}
        id={id}
        text={text}
        setzeText={setzeText}
        uebernehmen={textUebernehmen}
        oeffnen={() => k.setzeOffen(true)}
        schliessen={() => k.setzeOffen(false)}
        platz={t(platz)}
        /* Klein (in einer Tabellenzeile) ohne die Zahl der Miettage: Sie
           überdeckte dort das Ende des Zeitraums, und die Zeile nennt ihre
           Menge ohnehin in der Spalte daneben. */
        rechts={tage > 0 && !klein ? <span className="zr-tage">{t("zeitraum.tage", { zahl: tage })}</span> : null}
        klein={klein}
        gesperrt={gesperrt}
        titel={titel}
        schild={schild}
        ungueltig={falsch}
        beschrieben={hinweisId}
      />
      {/* **Der Satz steht unter dem Feld, nicht im Tooltip** (Prüfrunde
          Runde 12, N12-03): Das Feld trug `aria-invalid` und den Satz nur als
          Titel — sichtbar war nichts, nicht einmal der rote Rahmen. Der
          Rahmen kommt aus der einen Regel für jedes Eingabefeld
          (`packages/ui/src/basis.css`), der Satz von hier. */}
      {falsch ? (
        <p className="zr-hinweis" id={hinweisId} role="status">{t("zeitraum.nichtGelesen")}</p>
      ) : null}

      {k.offen ? (
        <Kalenderblatt
          blattRaum={k.blattRaum}
          lage={k.lage}
          schild={schnell ? undefined : "zeitraum.dauerSchild"}
          wahlen={wahlen}
          blatt={blatt}
          setzeBlatt={setzeBlatt}
          monate={2}
          rueckblick={rueckblick}
          tag={(iso) => ({
            rand: iso === wert.von || iso === wert.bis,
            drin: Boolean(wert.von && wert.bis && iso > wert.von && iso < wert.bis),
            klick: () => {
              /* Erster Klick setzt den Anfang, zweiter das Ende. Wer rückwärts
                 klickt, meint einen neuen Anfang — nicht ein Ende vor dem Anfang.

                 **Welcher Klick der zweite ist, merkt sich das Feld selbst**
                 (Prüfrunde Runde 11, „Neu gesehen"). Bis dahin las es das am
                 Wert ab: Anfang ohne Ende hieß „jetzt kommt das Ende". Die
                 Vorbelegung der Vermietfläche ist aber genau so ein Wert
                 (ab morgen, das Ende rechnet der Kalender) — der erste Klick
                 setzte dort das Ende zum vorbelegten Anfang, und in der Form
                 „Dauer" ließ sich der Anfang gar nicht nach vorn legen. */
              const klick = kalenderKlick(wert, iso, endeFolgt);
              aendern(klick.neu);
              setzeEndeFolgt(klick.endeFolgt);
              if (klick.fertig) k.setzeOffen(false);
            },
          })}
          heute={() => {
            /* Beim Vermieten setzt „Heute" den Anfang; beim Suchen blättert
               es nur dorthin — „ab heute" ist keine Frage an Belege. */
            if (schnell) {
              setzeBlatt(blattZu({ von: null, bis: null }, rueckblick));
              return;
            }
            const h = new Date();
            setzeBlatt({ jahr: h.getFullYear(), monat: h.getMonth() });
            aendern({ von: alsTag(h), bis: null });
          }}
          satz={wert.von && (endeFolgt || !wert.bis)
            ? t(schnell ? "zeitraum.jetztEndeSuche" : "zeitraum.jetztEnde")
            : t(schnell ? "zeitraum.hinweisSuche" : "zeitraum.hinweis")}
        />
      ) : null}
    </div>
  );
}

/* ══════════════ Datum — ein Tag ══════════════ */

/**
 * Wohin ein Tag meistens liegt — danach richtet sich die Spalte.
 *
 * `vor`: Frist, nächste Prüfung, verfügbar ab. `zurueck`: Anschaffung,
 * letzte Prüfung, der Tag einer nachgetragenen Einsatzzeit. Die Spalte
 * sagt dann „In einer Woche" oder „Vor einer Woche", und der Kalender
 * zeigt den Monat, in dem man sucht.
 */
export type Richtung = "vor" | "zurueck";

const TAGE_VOR = [
  { text: "zeitraum.heute", rechne: (h: string) => h },
  { text: "zeitraum.morgen", rechne: (h: string) => plusTage(h, 1) },
  { text: "zeitraum.inWoche", rechne: (h: string) => plusTage(h, 7) },
  { text: "zeitraum.inZweiWochen", rechne: (h: string) => plusTage(h, 14) },
  { text: "zeitraum.inMonat", rechne: (h: string) => plusMonate(h, 1) },
  { text: "zeitraum.inJahr", rechne: (h: string) => plusMonate(h, 12) },
];

const TAGE_ZURUECK = [
  { text: "zeitraum.heute", rechne: (h: string) => h },
  { text: "zeitraum.gestern", rechne: (h: string) => plusTage(h, -1) },
  { text: "zeitraum.vorWoche", rechne: (h: string) => plusTage(h, -7) },
  { text: "zeitraum.vorMonat", rechne: (h: string) => plusMonate(h, -1) },
  { text: "zeitraum.vorJahr", rechne: (h: string) => plusMonate(h, -12) },
];

function blattFuerTag(iso: string | null): Blattstand {
  const d = iso ? ausIso(iso) : new Date();
  return { jahr: d.getFullYear(), monat: d.getMonth() };
}

/**
 * Ein Tag — dasselbe Feld und dasselbe Blatt wie beim Zeitraum, mit einem
 * Monat statt zweien.
 *
 * Es ersetzt das Datumsfeld des Browsers (`type="date"`) überall, wo es
 * stand (18.09.2026): Bindefrist, Einsatzzeit, Prüfungen, Verfügbarkeit,
 * Anschaffung, Qualifikationen, „ab" am Telefon.
 *
 * **Leer ist erlaubt, wenn `leer` gesetzt ist** — der i18n-Schlüssel dessen,
 * was „kein Tag" hier heißt: „Offen" beim Ende der Verfügbarkeit,
 * „Unbefristet" bei einer Qualifikation. Er steht als letzte Zeile der
 * Spalte, und ein geleertes Feld heißt dasselbe. Ohne `leer` kehrt ein
 * geleertes Feld zum letzten Tag zurück.
 */
type DatumEigenschaften = {
  /** `JJJJ-MM-TT` oder `null`. */
  wert: string | null;
  aendern: (neu: string | null) => void;
  id?: string;
  richtung?: Richtung;
  leer?: string;
  platz?: string;
  gesperrt?: boolean | undefined;
  titel?: string | undefined;
  /** Das `aria-label` — am Telefon auch der Titel des Blattes. */
  schild?: string | undefined;
  klein?: boolean | undefined;
  ungueltig?: boolean | undefined;
  /** Eine Klasse mehr am Textfeld — für eine Breite, die die Maske schon kennt. */
  klasse?: string | undefined;
};

/** `2026-10-12T…` oder `2026-10-12` → der Tag; alles andere ist keiner. */
const nurTag = (wert: string | null): string | null =>
  (wert && tagImBetrieb(wert)) || null; // ein Zeitpunkt: sein deutscher Tag (N8-02)

/** Die Weiche — siehe `Zeitraum`. */
export function Datum(e: DatumEigenschaften) {
  return useTelefon() ? <DatumAmTelefon {...e} /> : <DatumFeld {...e} />;
}

/**
 * Ein Tag im Blatt von unten — der Kalender der Wiedervorlage
 * (`masken/handy/Zeitraumblatt.tsx`, `Tagesblatt`), mit dem, was `Datum`
 * darüber hinaus kann: einer Richtung und dem ausdrücklichen Weg zum Tippen.
 *
 * **Der Tipper ist die Wahl** — ein Tag im Kalender schließt das Blatt.
 * Unten steht trotzdem ein Feld: für den Tag, der zwölf Monate hinter der
 * Liste liegt (eine Anschaffung vor drei Jahren) oder den man einfach im
 * Kopf hat. Es holt den Ziffernblock (`inputMode="decimal"`), und zwar erst,
 * wenn jemand es antippt — genau die Unterscheidung, die Luca gezogen hat:
 * *„nicht die Tastatur triggern"* heißt nicht „nie tippen", sondern „nicht
 * ungefragt". Der Ziffernblock zählt nicht als Tippfeld
 * (`bausteine/Blattvonunten.tsx`, `TIPPFELD`), das Blatt bleibt also, was es
 * ist.
 *
 * Die Richtung sperrt die Vergangenheit: `vor` (Frist, Prüfung) beginnt
 * heute, `zurueck` (Einsatzzeit, Anschaffung) ein Jahr davor und reicht bis
 * heute. Der gewählte Tag steht in beiden Fällen im Bild, auch wenn er
 * außerhalb liegt — eine abgelaufene Frist soll man sehen, bevor man sie
 * neu setzt. Getippt werden darf jeder Tag: Wer ihn ausschreibt, meint ihn.
 *
 * Auch das Angebot am Telefon öffnet es direkt aus der Zeile „Bindefrist"
 * (`masken/vorgaenge/AngebotTelefon.tsx`) — ein Blatt mit einem Knopf, der
 * ein zweites Blatt öffnet, wäre ein Griff zu viel.
 */
export function Datumsblatt({ titel, unter, wert, richtung = "vor", leer, platz = "zeitraum.tagPlatz", schliessen, waehlen }: {
  titel: string;
  unter?: string | undefined;
  wert: string | null;
  richtung?: Richtung;
  /** Der i18n-Schlüssel dessen, was „kein Tag" hier heißt — dann steht er als Knopf im Fuß. */
  leer?: string | undefined;
  /** i18n-Schlüssel des Platzhalters im Tippfeld. */
  platz?: string;
  schliessen: () => void;
  waehlen: (neu: string | null) => void;
}) {
  const t = useT();
  const [text, setzeText] = useState("");
  const heute = heuteImBetrieb();
  const tag = nurTag(wert);
  const ab = richtung === "vor" ? heute : plusMonate(heute, -12);
  const getippt = tagAusText(text, new Date().getFullYear());
  const eintippen = () => { if (getippt) waehlen(getippt); };

  return (
    <Tagesblatt
      titel={titel}
      unter={unter}
      tag={tag}
      ab={ab}
      schliessen={schliessen}
      waehlen={waehlen}
      fuss={(
        <div className="hz-tippen">
          {leer ? (
            <button type="button" className="hz-tippen-leer" aria-pressed={tag === null} onClick={() => waehlen(null)}>
              {t(leer)}
            </button>
          ) : null}
          <div className="hz-tippen-zeile">
            <label className="hz-tippen-feld">
              <span className="hz-tippen-schild">{t("zeitraum.eintippen")}</span>
              <input
                className="feld"
                inputMode="decimal"
                autoComplete="off"
                enterKeyHint="done"
                placeholder={t(platz)}
                value={text}
                {...(text.trim() && !getippt ? { "aria-invalid": true as const } : {})}
                onChange={(e) => setzeText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); eintippen(); } }}
              />
            </label>
            {/* Der Ziffernblock am iPhone hat keine Eingabetaste — der Knopf
                ist dort der einzige Weg, das Getippte abzugeben. */}
            <button type="button" className="hz-tippen-knopf" disabled={!getippt} onClick={eintippen}>
              {t("zeitraum.eintippenUebernehmen")}
            </button>
          </div>
        </div>
      )}
    />
  );
}

/** Am Telefon: der Knopf und das `Datumsblatt`. */
function DatumAmTelefon({
  wert, aendern, id = "datum", richtung = "vor", leer, platz = "zeitraum.tagPlatz",
  gesperrt, titel, schild, klein, ungueltig, klasse,
}: DatumEigenschaften) {
  const t = useT();
  const [offen, setzeOffen] = useState(false);
  const tag = nurTag(wert);

  return (
    <div className="zr">
      <Kalenderknopf
        id={id}
        text={tag ? tagText(tag) : ""}
        platz={leer && !tag ? t(leer) : t(platz)}
        oeffnen={() => setzeOffen(true)}
        offen={offen}
        klein={klein}
        gesperrt={gesperrt}
        titel={titel}
        schild={schild}
        ungueltig={ungueltig}
        klasse={klasse}
      />
      {offen ? (
        <Datumsblatt
          titel={schild ?? t("zeitraum.tagTitel")}
          wert={tag}
          richtung={richtung}
          leer={leer}
          platz={platz}
          schliessen={() => setzeOffen(false)}
          waehlen={(neu) => { aendern(neu); setzeOffen(false); }}
        />
      ) : null}
    </div>
  );
}

function DatumFeld({
  wert, aendern, id = "datum", richtung = "vor", leer, platz = "zeitraum.tagPlatz",
  gesperrt, titel, schild, klein, ungueltig, klasse,
}: DatumEigenschaften) {
  const t = useT();
  const k = useKlappe();
  const tag = nurTag(wert);
  const anzeige = tag ? tagText(tag) : "";
  const [text, setzeText] = useState(anzeige);
  const [blatt, setzeBlatt] = useState(() => blattFuerTag(tag));

  useEffect(() => { setzeText(anzeige); }, [anzeige]);
  useLayoutEffect(() => { if (k.offen) k.nachmessen(); }, [blatt]);

  const waehlen = (neu: string | null) => {
    aendern(neu);
    if (neu) setzeBlatt(blattFuerTag(neu));
    k.setzeOffen(false);
  };

  const textUebernehmen = () => {
    const roh = text.trim();
    if (!roh) {
      if (leer) { if (tag !== null) aendern(null); } else setzeText(anzeige);
      return;
    }
    const neu = tagAusText(roh, new Date().getFullYear());
    if (!neu) { setzeText(anzeige); return; }
    if (neu !== tag) aendern(neu);
    else setzeText(anzeige);
  };

  const heute = heuteImBetrieb();
  const wahlen: Wahl[] = [
    ...(richtung === "vor" ? TAGE_VOR : TAGE_ZURUECK).map((w) => {
      const ziel = w.rechne(heute);
      return { text: w.text, gewaehlt: ziel === tag, waehlen: () => waehlen(ziel) };
    }),
    ...(leer ? [{ text: leer, gewaehlt: tag === null, waehlen: () => waehlen(null) }] : []),
  ];

  return (
    <div className="zr" ref={k.raum}>
      <Kalenderfeld
        feldRaum={k.feld}
        id={id}
        text={text}
        setzeText={setzeText}
        uebernehmen={textUebernehmen}
        oeffnen={() => { setzeBlatt(blattFuerTag(tag)); k.setzeOffen(true); }}
        schliessen={() => k.setzeOffen(false)}
        platz={leer && !tag ? t(leer) : t(platz)}
        klein={klein}
        gesperrt={gesperrt}
        titel={titel}
        schild={schild}
        ungueltig={ungueltig}
        klasse={klasse}
      />

      {k.offen ? (
        <Kalenderblatt
          blattRaum={k.blattRaum}
          lage={k.lage}
          wahlen={wahlen}
          blatt={blatt}
          setzeBlatt={setzeBlatt}
          monate={1}
          tag={(iso) => ({ rand: iso === tag, drin: false, klick: () => waehlen(iso) })}
          heute={() => setzeBlatt(blattFuerTag(null))}
          satz={t("zeitraum.hinweisTag")}
        />
      ) : null}
    </div>
  );
}

/* ══════════════ DatumZeit — ein Zeitpunkt ══════════════ */

/**
 * Ein Tag mit Uhrzeit — der Zeitpunkt einer Übergabe.
 *
 * Bis zum 18.09.2026 stand hier `type="datetime-local"`: Kalender und Uhr
 * des Browsers in einem Feld, in jedem Browser anders, am Mac ein Rad. Jetzt
 * ist der Tag das `Datum` der Anwendung und die Uhrzeit ein eigenes Feld
 * daneben. Der Wert bleibt, was er war — `JJJJ-MM-TTTHH:MM` —, damit sich an
 * den Masken, die ihn weiterreichen, nichts ändert.
 */
export function DatumZeit({ wert, aendern, id = "zeitpunkt", richtung = "zurueck", gesperrt }: {
  /** `JJJJ-MM-TTTHH:MM` */
  wert: string;
  aendern: (neu: string) => void;
  id?: string;
  richtung?: Richtung;
  gesperrt?: boolean | undefined;
}) {
  const t = useT();
  const [tag = "", zeit = ""] = wert.split("T");
  const uhr = zeit.slice(0, 5);
  return (
    <div className="zr-zeitpunkt">
      <Datum
        id={id}
        wert={tag || null}
        richtung={richtung}
        gesperrt={gesperrt}
        aendern={(neu) => { if (neu) aendern(`${neu}T${uhr || "00:00"}`); }}
      />
      <input
        className="feld zr-uhr"
        type="time"
        aria-label={t("zeitraum.uhrzeit")}
        value={uhr}
        disabled={gesperrt}
        onChange={(e) => { if (tag && e.target.value) aendern(`${tag}T${e.target.value}`); }}
      />
    </div>
  );
}
